import {
  brandOptions,
  categoriesOptions,
  colorOptions,
  conditionOptions,
  costumeBrandOptions,
  costumeBrandTypeOptions,
  costumeColorOptions,
  costumeGenreOptions,
  costumeListingTypeOptions,
  costumeSizeOptions,
  costumeTypeOptions,
  shankOptions,
  shoesBrandOptions,
  shoesColorOptions,
  shoesSizesOptions,
  shoesTypeOptions,
  sizeOptions,
  typeOptions,
  widthOptions,
} from '../marketplace-custom-config';
import { ListingCategory, ListingFieldConfigKey } from '../../util/enums';

const MAIN_LISTING_TYPE = {
  id: 'product-selling',
  label: 'Sell products',
  transactionProcess: {
    name: 'default-purchase',
    alias: 'default-purchase/release-1',
  },
  unitType: 'item',
};

const toEnumOption = item => {
  const rawOption = item?.option ?? item?.key ?? item?.values;
  const rawLabel = item?.label ?? rawOption;
  const option = rawOption != null ? `${rawOption}`.trim() : '';
  const label = rawLabel != null ? `${rawLabel}`.trim() : '';

  return option && label ? { option, label } : null;
};

const uniqueByOption = enumOptions => {
  const seen = new Set();
  return enumOptions.filter(option => {
    const key = option?.option;
    if (!key || seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

const toEnumOptions = list => uniqueByOption((list || []).map(toEnumOption).filter(Boolean));

const mergeEnumOptions = (...lists) => {
  const merged = lists.reduce((acc, list) => [...acc, ...(list || [])], []);
  return toEnumOptions(merged);
};

const categoryTree = (categoriesOptions || [])
  .map(category => {
    const id = category?.values ? `${category.values}` : null;
    const name = category?.label ? `${category.label}` : id;
    return id && name ? { id, name, subcategories: [] } : null;
  })
  .filter(Boolean);

const createField = ({
  key,
  label,
  enumOptions,
  schemaType = 'enum',
  categoryIdsInUse = [],
  limitToCategoryIds = false,
  filterConfig,
}) => ({
  key,
  scope: 'public',
  schemaType,
  enumOptions,
  ...(limitToCategoryIds
    ? {
        categoryConfig: {
          limitToCategoryIds: true,
          categoryIds: categoryIdsInUse,
        },
      }
    : {}),
  ...(filterConfig ? { filterConfig } : {}),
  showConfig: {
    label,
  },
  saveConfig: {
    label,
    placeholderMessage: 'Select an option…',
    required: false,
  },
});

const genericSizeOptions = mergeEnumOptions(sizeOptions, shoesSizesOptions, costumeSizeOptions);
const genericBrandOptions = mergeEnumOptions(brandOptions, shoesBrandOptions, costumeBrandOptions);
const genericColorOptions = mergeEnumOptions(colorOptions, shoesColorOptions, costumeColorOptions);
const genericTypeOptions = mergeEnumOptions(typeOptions, shoesTypeOptions, costumeTypeOptions);

const listingFields = [
  createField({
    key: 'category',
    label: 'Category',
    enumOptions: toEnumOptions(categoriesOptions),
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectSingleFilter',
      label: 'Category',
      group: 'primary',
    },
  }),
  createField({
    key: ListingFieldConfigKey.CONDITION,
    label: 'Condition',
    enumOptions: toEnumOptions(conditionOptions),
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Condition',
      group: 'primary',
    },
  }),
  createField({
    key: ListingFieldConfigKey.SIZE,
    label: 'Size',
    enumOptions: genericSizeOptions,
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Size',
      group: 'primary',
    },
  }),
  createField({
    key: ListingFieldConfigKey.BRAND,
    label: 'Brand',
    enumOptions: genericBrandOptions,
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand',
      group: 'primary',
    },
  }),
  createField({
    key: ListingFieldConfigKey.COLOR,
    label: 'Color',
    enumOptions: genericColorOptions,
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Color',
      group: 'primary',
    },
  }),
  createField({
    key: 'type',
    label: 'Type',
    enumOptions: genericTypeOptions,
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Type',
      group: 'primary',
    },
  }),
  createField({
    key: ListingFieldConfigKey.SHOES_SHANK,
    label: 'Shank',
    enumOptions: toEnumOptions(shankOptions),
    categoryIdsInUse: [ListingCategory.SHOES],
    limitToCategoryIds: true,
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Shank',
      group: 'primary',
    },
  }),
  createField({
    key: ListingFieldConfigKey.SHOES_WIDTH,
    label: 'Width',
    enumOptions: toEnumOptions(widthOptions),
    categoryIdsInUse: [ListingCategory.SHOES],
    limitToCategoryIds: true,
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Width',
      group: 'primary',
    },
  }),
  createField({
    key: ListingFieldConfigKey.GENRE,
    label: 'Genre',
    enumOptions: toEnumOptions(costumeGenreOptions),
    categoryIdsInUse: [ListingCategory.COSTUMES],
    limitToCategoryIds: true,
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Genre',
      group: 'primary',
    },
  }),
  createField({
    key: ListingFieldConfigKey.UNIT_OF_MEASUREMENT,
    label: 'Unit of measurement',
    enumOptions: toEnumOptions([
      { values: 'g', label: 'g' },
      { values: 'kg', label: 'kg' },
      { values: 'oz', label: 'oz' },
      { values: 'lb', label: 'lb' },
    ]),
  }),
  createField({
    key: ListingFieldConfigKey.DANCEWEAR_TYPE,
    label: 'Dancewear type',
    enumOptions: toEnumOptions(typeOptions),
    categoryIdsInUse: [ListingCategory.DANCEWEAR],
    limitToCategoryIds: true,
  }),
  createField({
    key: ListingFieldConfigKey.DANCEWEAR_SIZE,
    label: 'Dancewear size',
    enumOptions: toEnumOptions(sizeOptions),
    categoryIdsInUse: [ListingCategory.DANCEWEAR],
    limitToCategoryIds: true,
  }),
  createField({
    key: ListingFieldConfigKey.DANCEWEAR_BRAND,
    label: 'Dancewear brand',
    enumOptions: toEnumOptions(brandOptions),
    categoryIdsInUse: [ListingCategory.DANCEWEAR],
    limitToCategoryIds: true,
  }),
  createField({
    key: ListingFieldConfigKey.DANCEWEAR_COLOR,
    label: 'Dancewear color',
    enumOptions: toEnumOptions(colorOptions),
    categoryIdsInUse: [ListingCategory.DANCEWEAR],
    limitToCategoryIds: true,
  }),
  createField({
    key: ListingFieldConfigKey.SHOES_TYPE,
    label: 'Shoe type',
    enumOptions: toEnumOptions(shoesTypeOptions),
    categoryIdsInUse: [ListingCategory.SHOES],
    limitToCategoryIds: true,
  }),
  createField({
    key: ListingFieldConfigKey.SHOES_SIZE,
    label: 'Shoe size',
    enumOptions: toEnumOptions(shoesSizesOptions),
    categoryIdsInUse: [ListingCategory.SHOES],
    limitToCategoryIds: true,
  }),
  createField({
    key: ListingFieldConfigKey.SHOES_BRAND,
    label: 'Shoe brand',
    enumOptions: toEnumOptions(shoesBrandOptions),
    categoryIdsInUse: [ListingCategory.SHOES],
    limitToCategoryIds: true,
  }),
  createField({
    key: ListingFieldConfigKey.SHOES_COLOR,
    label: 'Shoe color',
    enumOptions: toEnumOptions(shoesColorOptions),
    categoryIdsInUse: [ListingCategory.SHOES],
    limitToCategoryIds: true,
  }),
  createField({
    key: ListingFieldConfigKey.COSTUMES_TYPE,
    label: 'Costume type',
    enumOptions: toEnumOptions(costumeTypeOptions),
    categoryIdsInUse: [ListingCategory.COSTUMES],
    limitToCategoryIds: true,
  }),
  createField({
    key: ListingFieldConfigKey.COSTUMES_SIZE,
    label: 'Costume size',
    enumOptions: toEnumOptions(costumeSizeOptions),
    categoryIdsInUse: [ListingCategory.COSTUMES],
    limitToCategoryIds: true,
  }),
  createField({
    key: ListingFieldConfigKey.COSTUMES_BRAND,
    label: 'Costume brand',
    enumOptions: toEnumOptions(costumeBrandOptions),
    categoryIdsInUse: [ListingCategory.COSTUMES],
    limitToCategoryIds: true,
  }),
  createField({
    key: ListingFieldConfigKey.COSTUMES_COLOR,
    label: 'Costume color',
    enumOptions: toEnumOptions(costumeColorOptions),
    categoryIdsInUse: [ListingCategory.COSTUMES],
    limitToCategoryIds: true,
  }),
  createField({
    key: ListingFieldConfigKey.COSTUMES_TYPE_OPTIONS,
    label: 'Costume listing type',
    enumOptions: toEnumOptions(costumeListingTypeOptions),
    categoryIdsInUse: [ListingCategory.COSTUMES],
    limitToCategoryIds: true,
  }),
  createField({
    key: ListingFieldConfigKey.COSTUMES_GENRE_OPTIONS,
    label: 'Costume genre',
    enumOptions: toEnumOptions(costumeGenreOptions),
    categoryIdsInUse: [ListingCategory.COSTUMES],
    limitToCategoryIds: true,
  }),
  createField({
    key: ListingFieldConfigKey.COSTUMES_BRAND_TYPE_OPTIONS,
    label: 'Costume brand type',
    enumOptions: toEnumOptions(costumeBrandTypeOptions),
    categoryIdsInUse: [ListingCategory.COSTUMES],
    limitToCategoryIds: true,
  }),
];

const search = {
  mainSearch: {
    searchType: 'keywords',
  },
  listingTypeFilter: {
    enabled: false,
    schemaType: 'listingType',
  },
  categoryFilter: {
    enabled: false,
    schemaType: 'category',
  },
  dateRangeFilter: {
    schemaType: 'dates',
    availability: 'time-full',
    dateRangeMode: 'day',
  },
  priceFilter: {
    schemaType: 'price',
    min: 0,
    max: 1000,
    step: 5,
  },
};

export const createStaticMarketplaceConfigAsset = () => {
  return {
    categories: { categories: categoryTree },
    listingTypes: { listingTypes: [MAIN_LISTING_TYPE] },
    listingFields: { listingFields },
    search,
  };
};
